"use client"
import React, { createContext, useContext, useState, useEffect } from "react"
import { useCategory } from "@/src/context/category/category-context"
import { useMerchant } from '@/src/context/merchant/merchant-context'

type VisibleCategoriesState = {
    [key: number]: boolean;
};

type VisibleCategoriesContextType = {
    visibleCategories: Record<number, boolean>
    toggleCategoryVisibility: (index: number) => void
    openCategory: (id: string) => void
    collapseAllCategories: () => void
    activeCategoryIndex: number | null
    setActiveCategoryIndex: (index: number | null) => void
    setFirstCategoryLoaded: (e: boolean) => void
    firstCategoryLoaded: boolean
    setIsScrollingToSection: (e: boolean) => void
    isScrollingToSection: boolean
};

export const VisibleCategoriesContext = createContext<VisibleCategoriesContextType | undefined>(undefined)

export const VisibleCategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { merchantSummary } = useMerchant()
    const [visibleCategories, setVisibleCategories] = useState<VisibleCategoriesState>({})
    const { categories } = useCategory()
    const [activeCategoryIndex, setActiveCategoryIndex] = useState<number | null>(null)
    const [firstCategoryLoaded, setFirstCategoryLoaded] = useState<boolean>(false)
    const [isScrollingToSection, setIsScrollingToSection] = useState(false)

    const toggleCategoryVisibility = (categoryId: number) => {
        setVisibleCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }))
    }

    const openCategory = (categoryId: string) => {
        const newVisibleState: VisibleCategoriesState = {}

        categories.forEach((category) => {
            const index = categories.findIndex((c) => c.id === category.id)
            if (index !== -1) {
                newVisibleState[index] = true
            }
        })

        setVisibleCategories(prev => ({
            ...prev,
            ...newVisibleState
        }))
    }

    const collapseAllCategories = () => {
        const collapsedState: VisibleCategoriesState = {}
        categories.forEach((_, index) => {
            collapsedState[index] = false
        })
        setVisibleCategories(collapsedState)
    }

    useEffect(() => {
        const initialState: VisibleCategoriesState = {}
        categories.forEach((_, index) => {
            initialState[index] = merchantSummary?.categoriesViewMode !== 'Compact'
        })
        setVisibleCategories(initialState)
    }, [categories])

    return (
        <VisibleCategoriesContext.Provider value={{
            visibleCategories, toggleCategoryVisibility,
            activeCategoryIndex, setActiveCategoryIndex,
            firstCategoryLoaded, setFirstCategoryLoaded,
            isScrollingToSection, setIsScrollingToSection,
            collapseAllCategories, openCategory
        }}>
            {children}
        </VisibleCategoriesContext.Provider>
    );
};

export const useVisibleCategories = () => {
    const context = useContext(VisibleCategoriesContext)
    if (!context) {
        throw new Error("useVisibleCategories must be used within a VisibleCategoriesProvider")
    }
    return context;
};